import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm, type FieldErrors } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Link as LinkIcon, Loader2, Pencil, Plus, Save, Search, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { StickyActionBar } from "@/components/layout/StickyActionBar";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { ESTADOS_BR } from "@/lib/estados";
import { buscarCep } from "@/lib/cep";
import { formatCep, formatCnpj, hojeMaisDias } from "@/lib/format";
import {
  deleteInstituicaoVinculada,
  deleteStakeholder,
  getInstituicaoRegistro,
  getInstituicoesVinculadas,
  getStakeholders,
  saveInstituicao,
  type InstituicaoRegistro,
  type InstituicaoVinculada,
  type Stakeholder,
} from "./formApi";
import { instituicaoFormSchema, type InstituicaoFormValues } from "./formSchema";

// Porte de app/controllers/form-instituicao.controller.js +
// app/templates/instituicao/form-instituicao.html — a maior tela do app.
//
// O select "Matriz/Filial" do legado foi removido: ele gravava `tipoMatrizFilial`,
// que não é coluna da entidade Instituicao no backend (a relação matriz/filial
// fica em MatrizFilialAdmin, ligada pelas instituições vinculadas), então nunca
// persistia — no legado ainda gravava `undefined` por causa do
// `ng-value="MATRIZ"` sem aspas. Ver D14 em docs/migracao/decisoes.md.
const DEFAULT_VALUES: InstituicaoFormValues = {
  situacaoAdmin: "T",
  situacaoCs: "ON",
  plano: "M",
  seBloqueiaDataHoraEvolucao: "S",
  seMedicamentoGeral: "S",
  seHomeCare: "N",
  seAppLiberado: "S",
  seControlarEstoque: "S",
  seProgramacaoAutomatica: "S",
  quantidadeDiasProgramacao: 5,
  seProgramacaoChecagem: "S",
  quantidadeDiasProgramacaoChecagem: 5,
  origemCadastro: "A",
  dataFimTrial: hojeMaisDias(3),
  razaoSocial: "",
  nomeFantasia: "",
  codigoUsuarioCliente: "",
};

const STAKEHOLDER_VAZIO: Stakeholder = { nome: "", sobrenome: "", email: "", celular: "", cargo: "" };
const VINCULADA_VAZIA: InstituicaoVinculada = { codigoVinculada: "", tipoInstituicaoVinculada: "" };

// Campos que só aparecem quando "Configurações" está expandido — usado para
// abrir a seção automaticamente se o submit falhar por um erro escondido nela.
const CAMPOS_CONFIGURACOES = new Set([
  "situacaoAdmin",
  "dataFimTrial",
  "diasTrial",
  "motivoCancelamento",
  "situacaoCs",
  "plano",
  "dataRenovacaoPlano",
  "seBloqueiaDataHoraEvolucao",
  "seMedicamentoGeral",
  "seHomeCare",
  "seControlarEstoque",
  "seAppLiberado",
  "seProgramacaoAutomatica",
  "quantidadeDiasProgramacao",
  "seProgramacaoChecagem",
  "quantidadeDiasProgramacaoChecagem",
  "seBloqueioFinanceiro",
  "dataIgnorarBloqueio",
]);

// Normaliza o registro do GET para o formato do form (datetime -> yyyy-MM-dd).
function valoresDoRegistro(registro: InstituicaoRegistro): InstituicaoFormValues {
  return {
    ...registro,
    dataInicio: registro.dataInicio?.slice(0, 10),
    dataFimTrial: registro.dataFimTrial?.slice(0, 10),
    dataRenovacaoPlano: registro.dataRenovacaoPlano?.slice(0, 10),
    dataIgnorarBloqueio: registro.dataIgnorarBloqueio?.slice(0, 10),
  };
}

// Na edição, o form só monta depois que o registro chegou e já nasce com os
// valores certos (defaultValues), sem `form.reset` depois da montagem. Com o
// registro vindo do cache, o reset rodava antes de os itens do Select do Radix
// se registrarem, e o Radix sobrescrevia os enums com "" (campos em branco e
// "campos inválidos" ao salvar). A query tem chave própria e gcTime 0 para o
// form nunca nascer com dado velho do cache.
export default function FormInstituicao() {
  const { idInstituicao } = useParams<{ idInstituicao: string }>();
  const isEdit = !!idInstituicao;

  const { data: registro } = useQuery({
    queryKey: ["instituicao", "registro-form", idInstituicao],
    queryFn: () => getInstituicaoRegistro(Number(idInstituicao)),
    enabled: isEdit,
    staleTime: 0,
    gcTime: 0,
  });

  if (isEdit && !registro) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Carregando...
      </div>
    );
  }

  return <FormInstituicaoCampos key={idInstituicao ?? "novo"} idInstituicao={idInstituicao} registro={registro} />;
}

function FormInstituicaoCampos({ idInstituicao, registro }: { idInstituicao?: string; registro?: InstituicaoRegistro }) {
  const isEdit = !!idInstituicao;
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [configuracaoAberta, setConfiguracaoAberta] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  // `null` = usuário ainda não mexeu na lista; vale o que veio do GET. Depois da
  // primeira alteração, a lista local passa a ser a única fonte (inclusive vazia).
  const [stakeholders, setStakeholders] = useState<Stakeholder[] | null>(null);
  const [stakeholderDialogOpen, setStakeholderDialogOpen] = useState(false);
  const [stakeholderEdicao, setStakeholderEdicao] = useState<{ index: number; valor: Stakeholder } | null>(null);

  const [vinculadas, setVinculadas] = useState<InstituicaoVinculada[] | null>(null);
  const [vinculadaDialogOpen, setVinculadaDialogOpen] = useState(false);
  const [vinculada, setVinculada] = useState<InstituicaoVinculada>(VINCULADA_VAZIA);

  const [buscandoCep, setBuscandoCep] = useState(false);

  const form = useForm<InstituicaoFormValues>({
    resolver: zodResolver(instituicaoFormSchema),
    defaultValues: registro ? valoresDoRegistro(registro) : DEFAULT_VALUES,
  });

  const { data: stakeholdersCarregados } = useQuery({
    queryKey: ["instituicao", "stakeholders", idInstituicao],
    queryFn: () => getStakeholders(Number(idInstituicao)),
    enabled: isEdit,
  });
  const { data: vinculadasCarregadas } = useQuery({
    queryKey: ["instituicao", "vinculadas", idInstituicao],
    queryFn: () => getInstituicoesVinculadas(Number(idInstituicao)),
    enabled: isEdit,
  });

  const stakeholdersEfetivos = stakeholders ?? stakeholdersCarregados ?? [];
  const vinculadasEfetivas = vinculadas ?? vinculadasCarregadas ?? [];
  const situacaoAdmin = form.watch("situacaoAdmin");
  const plano = form.watch("plano");
  const seProgramacaoAutomatica = form.watch("seProgramacaoAutomatica");
  const seProgramacaoChecagem = form.watch("seProgramacaoChecagem");

  const salvar = useMutation({
    mutationFn: (values: InstituicaoFormValues) =>
      // O zod descarta chaves fora do schema (codigo, franquiaId, auditoria...);
      // o legado devolvia a entidade inteira, então sobrepomos os valores
      // validados ao registro original em vez de enviar só o que o schema conhece.
      saveInstituicao({
        instituicao: { ...registro, ...values },
        stakeholders: stakeholdersEfetivos,
        instituicoesVinculadas: vinculadasEfetivas,
      }),
    onSuccess: () => {
      toast.success("Instituição salva.");
      qc.invalidateQueries({ queryKey: ["instituicao"] });
      navigate("/Instituicao/Consulta");
    },
  });

  async function handleBuscarCep() {
    const cep = form.getValues("cep") ?? "";
    if (!/^\d{5}-?\d{3}$/.test(cep)) {
      toast.error("CEP inválido!");
      return;
    }
    setBuscandoCep(true);
    try {
      const endereco = await buscarCep(cep);
      if (endereco.erro) {
        toast.error("CEP não encontrado");
        return;
      }
      form.setValue("rua", endereco.logradouro ?? "");
      form.setValue("bairro", endereco.bairro ?? "");
      form.setValue("municipio", endereco.localidade ?? "");
      form.setValue("estado", endereco.uf ?? "");
    } catch {
      toast.error("Erro na busca de CEP");
    } finally {
      setBuscandoCep(false);
    }
  }

  function abrirNovoStakeholder() {
    setStakeholderEdicao(null);
    setStakeholderDialogOpen(true);
  }

  function abrirEdicaoStakeholder(index: number, valor: Stakeholder) {
    setStakeholderEdicao({ index, valor: { ...valor } });
    setStakeholderDialogOpen(true);
  }

  function confirmarStakeholder(valor: Stakeholder) {
    if (!valor.nome || !valor.celular || !valor.cargo) {
      toast.warning("Preencha nome, celular e cargo.");
      return;
    }
    const base = stakeholdersEfetivos;
    if (stakeholderEdicao) {
      const next = [...base];
      next[stakeholderEdicao.index] = valor;
      setStakeholders(next);
    } else {
      setStakeholders([...base, valor]);
    }
    setStakeholderDialogOpen(false);
  }

  async function removerStakeholder(index: number, s: Stakeholder) {
    if (s.stakeholderInstituicaoId) {
      try {
        await deleteStakeholder(s.stakeholderInstituicaoId);
      } catch {
        toast.error("Não foi possível excluir o stakeholder. Tente novamente.");
        return;
      }
    }
    setStakeholders(stakeholdersEfetivos.filter((_, i) => i !== index));
  }

  function confirmarVinculada() {
    if (!vinculada.codigoVinculada || !vinculada.tipoInstituicaoVinculada) {
      toast.warning("Preencha código e tipo.");
      return;
    }
    const jaExiste = vinculadasEfetivas.some((x) => x.codigoVinculada.trim() === vinculada.codigoVinculada.trim());
    if (jaExiste) {
      toast.warning("Já existe uma vinculada com esse código.");
      return;
    }
    setVinculadas([...vinculadasEfetivas, vinculada]);
    setVinculadaDialogOpen(false);
  }

  async function removerVinculada(index: number, v: InstituicaoVinculada) {
    if (v.instituicaoVinculadaId) {
      try {
        await deleteInstituicaoVinculada(v.instituicaoVinculadaId);
      } catch {
        toast.error("Não foi possível excluir a instituição vinculada. Tente novamente.");
        return;
      }
    }
    setVinculadas(vinculadasEfetivas.filter((_, i) => i !== index));
  }

  function aoInvalidar(errors: FieldErrors<InstituicaoFormValues>) {
    if (Object.keys(errors).some((campo) => CAMPOS_CONFIGURACOES.has(campo))) {
      setConfiguracaoAberta(true);
    }
    toast.error("Existem campos inválidos. Verifique os campos destacados em vermelho.");
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((values) => salvar.mutate(values), aoInvalidar)} className="space-y-6 pb-20">
        <h1 className="text-lg font-semibold text-foreground">Cadastro de Instituição</h1>

        <h2 className="text-base font-semibold text-foreground">Informações da Instituição</h2>
        <div className="grid gap-4 sm:grid-cols-4">
          <FormField
            control={form.control}
            name="razaoSocial"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Razão Social</FormLabel>
                <FormControl>
                  <Input autoFocus {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="nomeFantasia"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nome Fantasia</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="cnpj"
            render={({ field }) => (
              <FormItem>
                <FormLabel>CNPJ</FormLabel>
                <FormControl>
                  <Input {...field} onChange={(e) => field.onChange(formatCnpj(e.target.value))} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="emailFinanceiro"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email Financeiro</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-4">
          <FormField
            control={form.control}
            name="privadaFilantropica"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tipo da Instituição</FormLabel>
                <Select value={field.value} onValueChange={(v) => v && field.onChange(v)}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="P">Privada</SelectItem>
                    <SelectItem value="V">Filantrópica</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="seParteDeAssociacao"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Faz parte de associação?</FormLabel>
                <Select value={field.value} onValueChange={(v) => v && field.onChange(v)}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="S">Sim</SelectItem>
                    <SelectItem value="N">Não</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="seSaoVicenteDePaulo"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Associado São Vicente de Paulo</FormLabel>
                <Select value={field.value} onValueChange={(v) => v && field.onChange(v)}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="S">Sim</SelectItem>
                    <SelectItem value="N">Não</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="dataInicio"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Data de início</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <h2 className="text-base font-semibold text-foreground">Contato Principal</h2>
        <div className="grid gap-4 sm:grid-cols-4">
          <FormField
            control={form.control}
            name="nomeContato"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Contato principal</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="telefone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Telefone</FormLabel>
                <FormControl>
                  <Input maxLength={20} {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="celular"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Celular</FormLabel>
                <FormControl>
                  <Input maxLength={20} {...field} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <h2 className="text-base font-semibold text-foreground">Endereço</h2>
        <div className="grid gap-4 sm:grid-cols-5">
          <FormField
            control={form.control}
            name="cep"
            render={({ field }) => (
              <FormItem>
                <FormLabel>CEP</FormLabel>
                <div className="flex gap-2">
                  <FormControl>
                    <Input {...field} onChange={(e) => field.onChange(formatCep(e.target.value))} />
                  </FormControl>
                  <Button type="button" variant="outline" size="icon" disabled={buscandoCep} onClick={handleBuscarCep}>
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="rua"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Endereço</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="bairro"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Bairro</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="municipio"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cidade</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="estado"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Estado</FormLabel>
                <Select value={field.value} onValueChange={(v) => v && field.onChange(v)}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o Estado" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {ESTADOS_BR.map((uf) => (
                      <SelectItem key={uf.value} value={uf.value}>
                        {uf.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
        </div>

        <h2 className="text-base font-semibold text-foreground">Usuário Sistema</h2>
        <FormField
          control={form.control}
          name="codigoUsuarioCliente"
          render={({ field }) => (
            <FormItem className="max-w-md">
              <FormLabel>Código usuário cliente</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="vazio: não cria, preenchido: codigousuario.codigoinstituicao"
                  onChange={(e) => field.onChange(e.target.value.normalize("NFD").replace(/[\u0300-\u036f]/g, ""))}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <button
          type="button"
          className="flex items-center gap-1 text-sm font-medium text-primary"
          onClick={() => setConfiguracaoAberta((v) => !v)}
        >
          Configurações <ChevronDown className={`h-4 w-4 transition-transform ${configuracaoAberta ? "rotate-180" : ""}`} />
        </button>

        {configuracaoAberta && (
          <div className="space-y-6 rounded-xl border border-border bg-card p-4">
            {isEdit && (
              <Button asChild variant="outline" size="sm">
                <Link to={`/Instituicao/Configuracao/${idInstituicao}`}>Configurações avançadas</Link>
              </Button>
            )}
            <div className="grid gap-4 sm:grid-cols-3">
              <FormField
                control={form.control}
                name="situacaoAdmin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Situação</FormLabel>
                    <Select value={field.value ?? undefined} onValueChange={(v) => v && field.onChange(v)}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="A">Ativo</SelectItem>
                        <SelectItem value="T">Trial</SelectItem>
                        <SelectItem value="I">Inativo</SelectItem>
                        <SelectItem value="C">Cancelado</SelectItem>
                        <SelectItem value="E">Teste</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
              {situacaoAdmin === "T" && (
                <>
                  <FormField
                    control={form.control}
                    name="dataFimTrial"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Último dia de trial</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="diasTrial"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Dias de Trial (campo antigo, só consulta)</FormLabel>
                        <FormControl>
                          <Input disabled {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </>
              )}
              {situacaoAdmin === "C" && (
                <FormField
                  control={form.control}
                  name="motivoCancelamento"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Motivo Cancelamento</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            <FormField
              control={form.control}
              name="situacaoCs"
              render={({ field }) => (
                <FormItem className="max-w-xs">
                  <FormLabel>Situação CS</FormLabel>
                  <Select value={field.value ?? undefined} onValueChange={(v) => v && field.onChange(v)}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="ON">Onboarding</SelectItem>
                      <SelectItem value="OG">Ongoing</SelectItem>
                      <SelectItem value="EX">Expansão</SelectItem>
                      <SelectItem value="RE">Retenção</SelectItem>
                      <SelectItem value="RC">Risco de Churn</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="plano"
                render={({ field }) => (
                  <FormItem className="max-w-xs">
                    <FormLabel>Plano</FormLabel>
                    <Select value={field.value ?? undefined} onValueChange={(v) => v && field.onChange(v)}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="M">Mensal</SelectItem>
                        <SelectItem value="S">Semestral</SelectItem>
                        <SelectItem value="A">Anual</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
              {(plano === "S" || plano === "A") && (
                <FormField
                  control={form.control}
                  name="dataRenovacaoPlano"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data de renovação</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}
            </div>

            {(
              [
                ["seBloqueiaDataHoraEvolucao", "Bloquear data e hora no lançamento de evolução"],
                ["seMedicamentoGeral", "Medicamento Geral"],
                ["seControlarEstoque", "Controlar estoque de medicamentos"],
                ["seAppLiberado", "Utilizar aplicativo"],
              ] as const
            ).map(([name, label]) => (
              <FormField
                key={name}
                control={form.control}
                name={name}
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox checked={field.value === "S"} onCheckedChange={(c) => field.onChange(c ? "S" : "N")} />
                    </FormControl>
                    <FormLabel className="!mt-0 font-normal">{label}</FormLabel>
                  </FormItem>
                )}
              />
            ))}

            <div className="flex flex-wrap items-center gap-4">
              <FormField
                control={form.control}
                name="seProgramacaoAutomatica"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox checked={field.value === "S"} onCheckedChange={(c) => field.onChange(c ? "S" : "N")} />
                    </FormControl>
                    <FormLabel className="!mt-0 font-normal">Programação de medicamentos automática</FormLabel>
                  </FormItem>
                )}
              />
              {seProgramacaoAutomatica === "S" && (
                <FormField
                  control={form.control}
                  name="quantidadeDiasProgramacao"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormLabel className="!mt-0">Dias de programação:</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={2}
                          max={15}
                          className="w-20"
                          {...field}
                          value={field.value ?? 5}
                          onChange={(e) => field.onChange(Math.min(15, Math.max(2, Number(e.target.value) || 2)))}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <FormField
                control={form.control}
                name="seProgramacaoChecagem"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox checked={field.value === "S"} onCheckedChange={(c) => field.onChange(c ? "S" : "N")} />
                    </FormControl>
                    <FormLabel className="!mt-0 font-normal">Programação de checagens automática</FormLabel>
                  </FormItem>
                )}
              />
              {seProgramacaoChecagem === "S" && (
                <FormField
                  control={form.control}
                  name="quantidadeDiasProgramacaoChecagem"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormLabel className="!mt-0">Dias de programação:</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={2}
                          max={15}
                          className="w-20"
                          {...field}
                          value={field.value ?? 5}
                          onChange={(e) => field.onChange(Math.min(15, Math.max(2, Number(e.target.value) || 2)))}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}
            </div>

            <div className="flex flex-wrap items-end gap-4">
              <FormField
                control={form.control}
                name="seBloqueioFinanceiro"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox checked={field.value === "S"} onCheckedChange={(c) => field.onChange(c ? "S" : "N")} />
                    </FormControl>
                    <FormLabel className="!mt-0 font-normal">Bloqueio Financeiro</FormLabel>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="dataIgnorarBloqueio"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Não bloquear até</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <Button type="button" variant="outline" onClick={abrirNovoStakeholder}>
                <Plus className="mr-2 h-4 w-4" /> Adicionar Stakeholder
              </Button>
              <Button type="button" variant="outline" onClick={() => { setVinculada(VINCULADA_VAZIA); setVinculadaDialogOpen(true); }}>
                <LinkIcon className="mr-2 h-4 w-4" /> Adicionar Instituição Vinculada
              </Button>
            </div>

            {stakeholdersEfetivos.length > 0 && (
              <div>
                <h3 className="mb-2 text-sm font-semibold">Stakeholders</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Sobrenome</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Celular</TableHead>
                      <TableHead>Cargo</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stakeholdersEfetivos.map((s, i) => (
                      <TableRow key={i}>
                        <TableCell>{s.nome}</TableCell>
                        <TableCell>{s.sobrenome}</TableCell>
                        <TableCell>{s.email}</TableCell>
                        <TableCell>{s.celular}</TableCell>
                        <TableCell>{s.cargo}</TableCell>
                        <TableCell className="flex gap-2">
                          <Button type="button" size="sm" variant="outline" onClick={() => abrirEdicaoStakeholder(i, s)}>
                            <Pencil className="mr-1 h-3 w-3" /> Editar
                          </Button>
                          <Button type="button" size="sm" variant="destructive" onClick={() => removerStakeholder(i, s)}>
                            <Trash2 className="mr-1 h-3 w-3" /> Remover
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {vinculadasEfetivas.length > 0 && (
              <div>
                <h3 className="mb-2 text-sm font-semibold">Instituições vinculadas</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código Instituição</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vinculadasEfetivas.map((v, i) => (
                      <TableRow key={i}>
                        <TableCell>{v.codigoVinculada}</TableCell>
                        <TableCell>{v.tipoInstituicaoVinculada === "MATRIZ" ? "Matriz" : v.tipoInstituicaoVinculada === "FILIAL" ? "Filial" : ""}</TableCell>
                        <TableCell>
                          <Button type="button" size="sm" variant="destructive" onClick={() => removerVinculada(i, v)}>
                            <Trash2 className="mr-1 h-3 w-3" /> Remover
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        <StickyActionBar>
          <Button type="submit" disabled={salvar.isPending}>
            {salvar.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {salvar.isPending ? "Salvando..." : "Salvar"}
          </Button>
          <Button type="button" variant="outline" onClick={() => setConfirmCancelOpen(true)}>
            Cancelar
          </Button>
        </StickyActionBar>
      </form>

      <ConfirmDialog
        open={confirmCancelOpen}
        onOpenChange={setConfirmCancelOpen}
        title="Cancelar alterações?"
        description="As alterações não salvas serão perdidas."
        confirmLabel="Sair sem salvar"
        onConfirm={() => navigate("/Instituicao/Consulta")}
      />

      <Dialog open={stakeholderDialogOpen} onOpenChange={setStakeholderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Stakeholder</DialogTitle>
          </DialogHeader>
          <StakeholderForm
            valorInicial={stakeholderEdicao?.valor ?? STAKEHOLDER_VAZIO}
            onCancelar={() => setStakeholderDialogOpen(false)}
            onSalvar={confirmarStakeholder}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={vinculadaDialogOpen} onOpenChange={setVinculadaDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar Instituição Vinculada</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-sm font-medium">Código da instituição</label>
              <Input
                placeholder="Ex: 1234"
                value={vinculada.codigoVinculada}
                onChange={(e) => setVinculada((v) => ({ ...v, codigoVinculada: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Tipo</label>
              <Select
                value={vinculada.tipoInstituicaoVinculada}
                onValueChange={(v) => setVinculada((prev) => ({ ...prev, tipoInstituicaoVinculada: v as "MATRIZ" | "FILIAL" }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MATRIZ">Matriz</SelectItem>
                  <SelectItem value="FILIAL">Filial</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setVinculadaDialogOpen(false)}>
              Cancelar
            </Button>
            <Button type="button" onClick={confirmarVinculada}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Form>
  );
}

function StakeholderForm({
  valorInicial,
  onCancelar,
  onSalvar,
}: {
  valorInicial: Stakeholder;
  onCancelar: () => void;
  onSalvar: (valor: Stakeholder) => void;
}) {
  const [valor, setValor] = useState(valorInicial);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label className="text-sm font-medium">Nome *</label>
          <Input value={valor.nome} onChange={(e) => setValor((v) => ({ ...v, nome: e.target.value }))} />
        </div>
        <div>
          <label className="text-sm font-medium">Sobrenome</label>
          <Input value={valor.sobrenome} onChange={(e) => setValor((v) => ({ ...v, sobrenome: e.target.value }))} />
        </div>
        <div>
          <label className="text-sm font-medium">Cargo *</label>
          <Input value={valor.cargo} onChange={(e) => setValor((v) => ({ ...v, cargo: e.target.value }))} />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="text-sm font-medium">Email</label>
          <Input type="email" value={valor.email} onChange={(e) => setValor((v) => ({ ...v, email: e.target.value }))} />
        </div>
        <div>
          <label className="text-sm font-medium">Celular *</label>
          <Input value={valor.celular} onChange={(e) => setValor((v) => ({ ...v, celular: e.target.value }))} />
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancelar}>
          Cancelar
        </Button>
        <Button type="button" onClick={() => onSalvar(valor)}>
          Salvar
        </Button>
      </DialogFooter>
    </div>
  );
}
